import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './FormComponent.scss';
import Schedule from './Schedule';
import Notification from './Notification';
import { ApplicationState } from '../../../store';
import { EmployeeScheduleRequest, ScheduleFormData, ShiftSchedule } from '../../../shared/models';
import { getEmpDetails, updateEmployeeSchedule } from '../../../store/empDetails/actions';
import DateHelper from '../../../helpers/DateHelper';

const initialFormData: ScheduleFormData = {
  name: '',
  employeeNumber: '',
  email: '',
  location: '',
  contract: '',
  shiftSchedule: [],
  goingTobe: []
};

const employeeScheduleRequest: EmployeeScheduleRequest = {
  reasonDescription: '',
  absentReasonId: '',
  email: '',
  employeeId: '',
  scheduleId: '',
  formSubmitedLocalDateTime: '',
  formUpdateLocalDateTime: '',
  scheduleStartTimeDate: '',
  scheduleStartTime: '',
  scheduleStartTimeString: '',
  scheduleEndTimeDate: '',
  scheduleEndTime: '',
  scheduleEndTimeString: '',
  scheduleHours: '',
  note: '',
  adminNote: '',
  IsScheduleCorrect:true,
  IsJuryDuty:false,
  IsBereavement:false,
  IsApplyPTO: false // Default value for IsApplyPTO
};

const FormComponent: React.FC = () => {
  const dispatch = useDispatch();
  const { employeeDetail: employeeDetails, schedules: empScheduleDetails, absentDetails } = useSelector((state: ApplicationState) => state.empData);
  const loggedUserDetails = useSelector((state: ApplicationState) => state.internalUser);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);

  const [isSubmited, setIsSubmited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<ScheduleFormData>(initialFormData);
  const [postData, setPostData] = useState(employeeScheduleRequest);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isApiCalled, setIsApiCalled] = useState(false);
  const [isError, setIsError] = useState(true);
  const [notificationText, setNotificationText] = useState("Notification Sent");
  const [notificationColor, setNotificationColor] = useState("#333");
  const [notificationBgColor] = useState("#e6ffe6");

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);

        if (!employeeDetails?.employeeId && loggedUserDetails?.idToken?.employeeid && loggedUserDetails?.idToken?.useremail && isApiCalled === false) {
          dispatch(getEmpDetails(loggedUserDetails.idToken?.employeeid, loggedUserDetails.idToken?.useremail) as any).then(_ => { setLoading(false); setIsApiCalled(true) });
        }
console.log(employeeDetails.employeeId);
        const updatedFormData = {
          name: `${employeeDetails.employeeFirstName} ${employeeDetails.employeeLastName}`,
          employeeNumber: employeeDetails.employeeId,
          email: employeeDetails.userPrincipalName,
          location: employeeDetails.stationName,
          contract: employeeDetails.contractName,
          shiftSchedule: empScheduleDetails,
          goingTobe: absentDetails
        };

        setFormData(updatedFormData);

        setPostData((prevData) => ({
          ...prevData,
          email: employeeDetails?.userPrincipalName,
          employeeId: employeeDetails?.employeeId,
        }));

      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        if (employeeDetails.employeeId !== '') {
          setLoading(false);
        }
      }
    };

    fetchEmployeeData();
  }, [absentDetails, dispatch, empScheduleDetails, employeeDetails, employeeDetails.employeeId, loggedUserDetails.employeeid,
    loggedUserDetails.idToken?.employeeid, loggedUserDetails.idToken?.useremail, loggedUserDetails.useremail]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target;

    if (e.target instanceof HTMLSelectElement) {
      const selectedText = e.target.options[e.target.selectedIndex].text;
      setPostData((prevData) => ({
        ...prevData,
        absentReasonId: value,
        reasonDescription: selectedText,
      }));
    }
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>, field: 'note' | 'adminNote') => {
    const { value } = event.target;
    setPostData((prevData) => ({ ...prevData, [field]: value }));
  };

  
  

  const handleScheduleSelection = (selectedSchedule: ShiftSchedule) => {
    fillScheduleFields(selectedSchedule);
    setSelectedScheduleId(selectedSchedule.scheduleId); // Track selected schedule
  };

  const fillScheduleFields = (selectedSchedule: ShiftSchedule) => {
    const { stringSchedule_StartTime, stringSchedule_EndTime, scheduleId } = selectedSchedule;

    // Use the helper function to get the schedule fields
    const {
      scheduleStartTimeDate,
      scheduleStartTimeString,
      scheduleEndTimeDate,
      scheduleEndTimeString,
      scheduleHours,
    } = DateHelper.extractScheduleFields(stringSchedule_StartTime, stringSchedule_EndTime);

  // Update postData with the extracted values
    setPostData((prevData: EmployeeScheduleRequest) => ({
      ...prevData,
      scheduleId: scheduleId,
      scheduleStartTimeDate,
      scheduleStartTime: stringSchedule_StartTime,
      scheduleStartTimeString,
      scheduleEndTimeDate,
      scheduleEndTime: stringSchedule_EndTime,
      scheduleEndTimeString,
      scheduleHours: scheduleHours,
      formSubmitedLocalDateTime: new Date().toISOString(),
      formUpdateLocalDateTime: new Date().toISOString(),
    }));
  };

  // Helper function to set notification details
  const setNotification = (text: string, color: string, isError:boolean = true) => {
    setNotificationText(text);
    setNotificationColor(color);
    setIsNotificationVisible(true);
    setIsError(isError);
    setIsSubmited(false);
  };

  const validatePostData = () => {
    // Validation checks
    if (postData.scheduleId === '') {
      setNotification("Required: Please select a Schedule.", "red");
      return false;
    }

    if (postData.absentReasonId === '') {
      setNotification("Required: Please select your answer for: Are you going to be?", "red");
      return false;
    }

    // if (postData.note === '') {
    //   setNotification("Required: Note to Shift Manager", "red");
    //   return false;
    // }

    if (!postData.IsScheduleCorrect && postData.note.trim() === '') {
      setNotification("Required: Note to Station is mandatory if the schedule is incorrect.", "red");
      return false;
    }

    // If everything is valid
   // setNotification("Sending Notification...", "#333", false);
    return true;
  };

  // Usage of the validatePostData function before performing the desired action
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsNotificationVisible(false);
    setIsSubmited(true);
    if (validatePostData()) {
      // Proceed with submission or further actions
      dispatch(updateEmployeeSchedule(postData) as any).then(response => {
        if (response === 'Success') {
          setNotification(postData.reasonDescription+" notification for "+ DateHelper.scheduleDateFormat(new Date(postData.scheduleStartTime))+" was sent successfully.", "#07752f", false);
          //The absence notification for Monday, September 23rd at 5:00 AM was sent successfully
          setPostData((prevData) => ({
            ...prevData,
            note: '',
            adminNote: '',
            absentReasonId: '',
            IsScheduleCorrect:true,
            IsJuryDuty:false,
            IsBereavement:false
          }));
         // Reset schedule to the first option after submission
         if (formData.shiftSchedule.length > 0) {
          fillScheduleFields(formData.shiftSchedule[0]);
          setSelectedScheduleId(formData.shiftSchedule[0].scheduleId); // Set the first schedule as selected
        }
        }
      })
    }
  };

  const handleNotificationClose = () => {
    setIsNotificationVisible(false);
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;
  setPostData((prevData) => {
    if (
      name === "IsScheduleCorrect" ||
      name === "IsJuryDuty" ||
      name === "IsBereavement" ||
      name === "IsApplyPTO"
    ) {
      return { ...prevData, [name]: value === "true" };
    }
    return { ...prevData, [name]: value };
  });
};

  return (
    <div>
       {(formData.name === "null null" || employeeDetails.employeeId === "") && loading === false ? ( 
          <div className="justify-content-center align-items-center mt-5">
            <div className="display-8 text-center">Oops! Something went wrong. We're unable to retrieve the data at the moment.</div>
            <div className="display-8 text-center">Please try again later or contact your manager to report this absence or late event.</div>
          </div>
        ):(
          
        <div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center"><div className="display-8 animate-text">Loading employee data...</div></div>
      ) : (
          <form className="form-container" onSubmit={handleSubmit}>
            {isNotificationVisible && (
              <Notification
                message={notificationText}
                backgroundColor={notificationBgColor}
                textColor={notificationColor}
                onClose={handleNotificationClose}
                isError={isError}
              />
            )}

           

            <div className="userinfo">
              <div className="uname">Welcome {formData.name} ({formData.employeeNumber})</div>
              
              {formData.shiftSchedule.length > 0 ?(
              <div className="contactInfo">
                <div><strong>Location:</strong> {formData.location || "No location provided"}</div>
                <div><strong>Contract:</strong> {formData.contract}</div>
              </div>
              ):(
                <div className="text-danger">
                  <strong>Error:</strong> You do not have a schedule available. Please contact your manager to report this absence or late event.
                </div>
              )}
            </div>
            
            {formData.shiftSchedule.length > 0 &&(
                <div>
                  <div className="form-group font-weight-light">
                    <label htmlFor="shiftSchedule">* Select a schedule (Please note that the schedule reflected is in the Kronos payroll system):</label>
                    <Schedule schedules={formData.shiftSchedule} onScheduleSelect={handleScheduleSelection} selectedScheduleId={selectedScheduleId} />
                  </div>

                  {/* radio for IsScheduleCorrect yes should be selected by default  */}
                  <div className="form-group">
                    <label>Is the selected schedule listed above, correct? If not, please add your shift details in the 'Note to Station' section. Please note that Unifi will still adhere to our attendance policy, and the 'Notes' section is only for providing additional information.</label>
                    <div>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsScheduleCorrect" value="true" checked={postData.IsScheduleCorrect} onChange={handleRadioChange} />
                        Yes
                      </label>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsScheduleCorrect" value="false" checked={!postData.IsScheduleCorrect} onChange={handleRadioChange} />
                        No
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="goingTobe">* Please select below if you will be late or Absent:</label>
                    <div className='custom-dropdown'>
                      <select name="absentReasonId" value={postData.absentReasonId} onChange={handleInputChange}>
                        <option value="">Select your option</option>
                        {formData.goingTobe.map((schedule, index) => (
                          <option key={index} value={schedule.absentReasonId}>{schedule.reasonDescription}</option>
                        ))}
                      </select>
                      <div className="arrow-container">
                        <div className="arrow"></div>
                      </div>
                    </div>
                  </div>

                      


                  <div className="form-group">
                    <label>
                      {(!postData.IsScheduleCorrect && postData.note.trim() === '')?"* ":""}What is the reason for your late or absence, any notes for this event? - Please provide any additional information that needs to be shared with your shift leader. Do not include personal medical details. If there are any special circumstances or updates regarding your employment status, please make sure to mention them here :
                    </label>
                    <textarea
                      rows={3}
                      cols={50}
                      name="noteToManager"
                      maxLength={500}
                      value={postData.note}
                      onChange={(e) => handleNoteChange(e, 'note')} />
                  </div>

                  <div className="form-group hidden">
                    <label> Note to Admin - (Any additional action required? Please, No Personal Medical Details.):</label>
                    <textarea disabled
                      rows={2}
                      cols={50}
                      name="noteToAdmin"
                      maxLength={500}
                      value={postData.adminNote}
                      onChange={(e) => handleNoteChange(e, 'adminNote')} />
                  </div>



                   {employeeDetails.isActiveQuestionApplyPTO === "True" && (
                      <div className="form-group">
                        <label>
                          Where available, would you like to apply any available PTO or other time balance to this absence?
                        </label>
                        <div>
                          <label className="ml-2">
                            <input
                              className="mr-1"
                              type="radio"
                              name="IsApplyPTO"
                              value="true"
                              checked={postData.IsApplyPTO === true}
                              onChange={handleRadioChange}
                            />
                            Yes
                          </label>
                          <label className="ml-2">
                            <input
                              className="mr-1"
                              type="radio"
                              name="IsApplyPTO"
                              value="false"
                              checked={postData.IsApplyPTO === false}
                              onChange={handleRadioChange}
                            />
                            No
                          </label>
                        </div>
                      </div>
                    )}     

                   {/* radio for IsJuryDuty No should be selected by default  */}
                   <div className="form-group">
                    <label>Is this callout related to jury duty? <span className='font-italic'>¹</span></label>
                    <div>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsJuryDuty" value="true" checked={postData.IsJuryDuty} onChange={handleRadioChange} />
                        Yes
                      </label>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsJuryDuty" value="false" checked={!postData.IsJuryDuty} onChange={handleRadioChange} />
                        No
                      </label>
                    </div>
                  </div>
                  {/* radio for IsBereavement yes should be selected by default  */}
                  <div className="form-group">
                    <label>Is this callout Related to bereavement? <span className='font-italic'>¹</span></label>
                    <div>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsBereavement" value="true" checked={postData.IsBereavement} onChange={handleRadioChange} />
                        Yes
                      </label>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsBereavement" value="false" checked={!postData.IsBereavement} onChange={handleRadioChange} />
                        No
                      </label>
                    </div>
                  </div>      


                  <div className="form-group font-italic mt-5">
                    <label>¹ In case you selected Yes Jury duty or Bereavement, please ensure that the documentation is submitted to local leaders before the pay period closes to avoid any issues with missed pay, in accordance with the handbook policy.</label>
                  </div>       
                         


                  <div className="d-flex justify-content-center mt-3 mb-3">
                    <button className={`notifyBtn ${isSubmited ? 'btndisabled' : ''}`} disabled={isSubmited} type="submit">{isSubmited ? 'Notifying' : 'Notify'}</button>
                  </div>
                 
                </div>
          
          )}           


        </form>
        
      )}
      </div>
       )}
    </div>
  );
};

export default FormComponent;